import React, { useState } from 'react';

import { useNavigate } from 'react-router';

import { Box, Typography, useTheme, Button } from '@mui/material';
import { CustomAvatar } from '@europrocurement/flexy-components';

import { JointureUtilisateurGroupeTravailJsonldUserworkgroupRead } from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import { downIcon, FaOptionIcon, upIcon } from '@europrocurement/l2d-icons';
import { utilisateursUtilisateursPath } from '../../../../../constants/paths';

export type MembersWidgetProps = {
    member: JointureUtilisateurGroupeTravailJsonldUserworkgroupRead;
};

export const MembersWidget: React.FunctionComponent<MembersWidgetProps> = function ({ member }) {
    const theme = useTheme();
    const navigate = useNavigate();

    const [displayDate, setDisplayDate] = useState<boolean>(false);

    const developWidget = () => {
        setDisplayDate(!displayDate);
    };

    const handleNavUser = () => {
        navigate(`${utilisateursUtilisateursPath}${member.user?.id}`);
    };

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="start"
            sx={{
                margin: '0px',
                padding: '12px',
                backgroundColor: theme.palette.background.default,
                borderRadius: '8px',
            }}
        >
            {/* Base container */}
            <Box
                display="flex"
                alignItems="center"
                justifyContent="start"
                width="100%"
            >
                {/* Avatar container */}
                <Box
                    sx={{
                        marginRight: '20px',
                    }}
                >
                    <CustomAvatar
                        firstName={member.user?.prenom}
                        lastName={member.user?.nom}
                        keycloakId={member.user?.userIdentifier || undefined}
                        sx={{ width: 50, height: 50, borderRadius: '50px' }}
                    />
                </Box>

                {/* Name + Mail container */}
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="start"
                    sx={{
                        marginRight: '10px',
                        width: '80%',
                    }}
                    onClick={handleNavUser}
                >
                    <Typography
                        variant="l2dh7"
                        sx={{
                            lineHeight: '1.4',
                            ':hover': {
                                cursor: 'pointer',
                                color: theme.palette.primary.dark,
                            },
                        }}
                    >
                        {member.user?.prenom} {member.user?.nom}
                    </Typography>

                    <Typography
                        data-testid="idUser"
                        variant="l2dbodyxs"
                        color="text.gris50"
                    >
                        {member.user?.mail}
                    </Typography>
                </Box>

                {/* Dropdown icon container */}
                <Box>
                    <Typography
                        data-testid="idUser"
                        variant="l2dbodyxs"
                        color="text.gris50"
                    >
                        <Button
                            aria-label="toggle-member-infos"
                            onClick={() => {
                                developWidget();
                            }}
                        >
                            {displayDate ? (
                                <FaOptionIcon
                                    {...upIcon.props}
                                    sx={{
                                        fontSize: 18,
                                        color: theme.palette.grey[600],
                                    }}
                                />
                            ) : (
                                <FaOptionIcon
                                    {...downIcon.props}
                                    sx={{
                                        fontSize: 18,
                                        color: theme.palette.grey[600],
                                    }}
                                />
                            )}
                        </Button>
                    </Typography>
                </Box>
            </Box>

            {/* Extended container */}
            {displayDate && (
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                    width="100%"
                >
                    {/* Inscription (start) */}
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        width="100%"
                        paddingLeft="70px"
                        margin="5px 0"
                    >
                        <Typography
                            variant="l2dbodys"
                            width="50%"
                            color="text.gris70"
                        >
                            Membre depuis le :
                        </Typography>
                        <Typography
                            variant="l2dbodys"
                            color="text.primary"
                            width="50%"
                        >
                            {member.dDebut
                                ? new Date(member.dDebut).toLocaleDateString('fr-FR')
                                : '-'}
                        </Typography>
                    </Box>

                    {/* Inscription (end) */}
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        width="100%"
                        paddingLeft="70px"
                        margin="5px 0"
                    >
                        <Typography
                            variant="l2dbodys"
                            width="50%"
                            color="text.gris70"
                        >
                            Jusqu&apos;au :
                        </Typography>
                        <Typography
                            variant="l2dbodys"
                            color="text.primary"
                            width="50%"
                        >
                            {member.dFin ? new Date(member.dFin).toLocaleDateString('fr-FR') : '-'}
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
};
